import React, { useCallback, useEffect, useState } from "react";
import "../../static/css/App.css";
import "../../static/css/customerTable.css";
import "bootstrap/dist/css/bootstrap.min.css";

import { Container, Button, Modal, Form, Row, Col } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import moment from "moment";
import { showConfirm } from "react-confirm-prompt";

import DynamicBreadcrumb from "../header/breadcrumb";
import Loader from "../loader/loader";
import SearchableDropdown from "../components/SearchableDropdown";

import branchApi from "../../services/branchapi";
import shippingChargeItemMappingsApi from "../../services/shippingChargeItemMappingsApi";

const ShippingChargeItemMappingsList = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);

    const [refreshTable, setRefreshTable] = useState(false);
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });

    const [scope, setScope] = useState("ALL");
    const [status, setStatus] = useState("ALL");
    const [chargeType, setChargeType] = useState("ALL");

    const [branchId, setBranchId] = useState(0);
    const [branchName, setBranchName] = useState("All");
    const [allBranches, setAllBranches] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyRows, setHistoryRows] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const [form, setForm] = useState({
        scope: "GLOBAL",
        branch_id: null,
        charge_type: "DELIVERY_CHARGE",
        charge_amount: "",
        item_ref_code: "",
        item_name: "Shipping Charge",
        item_price: "",
        status: 1,
    });

    const [errors, setErrors] = useState({});
    const [loaderVisible, setLoaderVisible] = useState(false);

    const pathArray = [{ name: "Shipping Charge Item Mapping", url: null }];

    const resetForm = () => {
        setForm({
            scope: "GLOBAL",
            branch_id: null,
            charge_type: "DELIVERY_CHARGE",
            charge_amount: "",
            item_ref_code: "",
            item_name: "Shipping Charge",
            item_price: "",
            status: 1,
        });
        setErrors({});
    };

    const openAddModal = () => {
        setEditingRow(null);
        resetForm();
        setShowModal(true);
    };

    const openEditModal = (row) => {
        setEditingRow(row);
        setForm({
            scope: row.scope || "GLOBAL",
            branch_id: row.branch_id || null,
            charge_type: row.charge_type || "DELIVERY_CHARGE",
            charge_amount: row.charge_amount ?? "",
            item_ref_code: row.item_ref_code || "",
            item_name:
                row.item_name ||
                (row.charge_type === "SMART_CART_FEE" ? "Smart Cart Fee" : "Shipping Charge"),
            item_price: row.item_price ?? "",
            status: Number(row.status ?? 1),
        });
        setErrors({});
        setShowModal(true);
    };

    const validateForm = () => {
        const e = {};
        if (!form.scope) e.scope = "Scope is required";
        if (form.scope === "BRANCH" && (!form.branch_id || Number(form.branch_id) <= 0)) {
            e.branch_id = "Branch is required";
        }
        if (!form.charge_type) {
            e.charge_type = "Charge type is required";
        }
        if (!form.charge_amount || Number(form.charge_amount) <= 0) {
            e.charge_amount = "Charge amount must be greater than 0";
        }
        if (!form.item_price || Number(form.item_price) <= 0) {
            e.item_price = "Item price must be greater than 0";
        }
        if (!form.item_ref_code?.trim()) {
            e.item_ref_code = "Item ref code is required";
        }
        if (!form.item_name?.trim()) {
            e.item_name = "Item name is required";
        }
        return e;
    };

    const deriveSearch = () => {
        const q = filterModel?.quickFilterValues?.[0];
        return q ? String(q) : null;
    };

    const mapScopeToApi = () => (scope === "ALL" ? null : scope);
    const mapStatusToApi = () => (status === "ALL" ? null : status);
    const mapChargeTypeToApi = () => (chargeType === "ALL" ? null : chargeType);

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const resp = await branchApi.getAllBranches({ status: 1, limit: 500, page: 0 });
                const list = (resp?.data?.result || []).sort((a, b) =>
                    (a.branchName || "").localeCompare(b.branchName || "")
                );
                setAllBranches(list);
            } catch (err) {
                console.error("Failed to fetch branches:", err);
            }
        };
        fetchBranches();
    }, []);

    const fetchMappings = useCallback(async () => {
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;

            const resp = await shippingChargeItemMappingsApi.list({
                limit: pageSize,
                page,
                search: deriveSearch(),
                scope: mapScopeToApi(),
                status: mapStatusToApi(),
                charge_type: mapChargeTypeToApi(),
                branch_id: branchId && Number(branchId) > 0 ? Number(branchId) : null,
                sortModel,
                filterModel,
            });

            const result = resp?.data?.result || [];
            const total = resp?.data?.totalRecords?.[0]?.total_records || 0;

            const formatted = result.map((r) => ({
                id: r.id,
                scope: r.scope,
                branch_id: r.branch_id,
                branch_name: r.branch_id
                    ? (allBranches.find((b) => Number(b.branchId) === Number(r.branch_id))?.branchName || `Branch #${r.branch_id}`)
                    : "GLOBAL",
                charge_type: r.charge_type || "DELIVERY_CHARGE",
                charge_amount: Number(r.charge_amount),
                item_ref_code: r.item_ref_code,
                item_name: r.item_name,
                item_price: Number(r.item_price),
                status: r.status,
                created_by_name: r.created_by_name || "-",
                updated_by_name: r.updated_by_name || "-",
                created_at: r.created_at ? moment(r.created_at).format("YYYY/MM/DD HH:mm:ss") : "",
                updated_at: r.updated_at ? moment(r.updated_at).format("YYYY/MM/DD HH:mm:ss") : "",
            }));

            setRows(formatted);
            setTotalRecords(total);
        } catch (err) {
            console.error("Error fetching mappings:", err);
            setRows([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    }, [paginationModel, sortModel, filterModel, refreshTable, scope, status, chargeType, branchId, allBranches]);

    useEffect(() => {
        fetchMappings();
    }, [fetchMappings]);

    const handleSave = async (e) => {
        e.preventDefault();

        const eMap = validateForm();
        if (Object.keys(eMap).length > 0) {
            setErrors(eMap);
            return;
        }

        setErrors({});

        try {
            setLoaderVisible(true);

            const payload = {
                scope: form.scope,
                branch_id: form.scope === "BRANCH" ? Number(form.branch_id) : null,
                charge_type: form.charge_type,
                charge_amount: Number(form.charge_amount).toFixed(2),
                item_ref_code: form.item_ref_code.trim(),
                item_name: form.item_name.trim(),
                item_price: Number(form.item_price).toFixed(2),
                status: Number(form.status),
            };

            let resp;
            if (editingRow?.id) {
                resp = await shippingChargeItemMappingsApi.update(editingRow.id, payload);
            } else {
                resp = await shippingChargeItemMappingsApi.create(payload);
            }

            if (resp?.status === "SUCCESS" || resp?.success === true) {
                setShowModal(false);
                resetForm();
                setEditingRow(null);

                if (paginationModel.page !== 0) {
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                } else {
                    await fetchMappings();
                }

                setRefreshTable((p) => !p);

                setTimeout(() => {
                    showConfirm("Success", {
                        description: resp?.message || "Saved successfully",
                        type: "Success",
                        hideCancel: true,
                    });
                }, 150);
            } else {
                setShowModal(false);
                setTimeout(() => {
                    showConfirm("Oops", {
                        description: resp?.message || "Something went wrong",
                        type: "error",
                        hideCancel: true,
                    });
                }, 200);
            }
        } catch (err) {
            showConfirm("Oops", {
                description: err?.response?.data?.message || err.message || "Save failed",
                type: "error",
                hideCancel: true,
            });
        } finally {
            setLoaderVisible(false);
        }
    };

    const toggleStatus = async (row) => {
        try {
            const nextStatus = Number(row.status) === 1 ? 0 : 1;
            const resp = await shippingChargeItemMappingsApi.updateStatus(row.id, nextStatus);

            if (resp?.status === "SUCCESS" || resp?.success === true) {
                await fetchMappings();
                setRefreshTable((p) => !p);
            } else {
                showConfirm("Oops", {
                    description: resp?.message || "Status update failed",
                    type: "error",
                    hideCancel: true,
                });
            }
        } catch (err) {
            showConfirm("Oops", {
                description: err?.response?.data?.message || err.message || "Status update failed",
                type: "error",
                hideCancel: true,
            });
        }
    };

    const openHistoryModal = async (row) => {
        try {
            setHistoryLoading(true);
            setShowHistoryModal(true);
            const resp = await shippingChargeItemMappingsApi.history(row.id);
            const result = resp?.data || [];

            const formatted = result.map((r) => ({
                id: r.id,
                action: r.action,
                changed_by_name: r.changed_by_name || "-",
                changed_ip: r.changed_ip || "-",
                created_at: r.created_at ? moment(r.created_at).format("YYYY/MM/DD HH:mm:ss") : "",
                old_data: r.old_data ? JSON.stringify(r.old_data, null, 2) : "-",
                new_data: r.new_data ? JSON.stringify(r.new_data, null, 2) : "-",
            }));

            setHistoryRows(formatted);
        } catch (err) {
            showConfirm("Oops", {
                description: err?.response?.data?.message || err.message || "Failed to load history",
                type: "error",
                hideCancel: true,
            });
            setHistoryRows([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const columns = [
        { field: "id", headerName: "ID", width: 80 },
        { field: "scope", headerName: "Scope", width: 120 },
        { field: "charge_type", headerName: "Charge Type", width: 170 },
        { field: "branch_name", headerName: "Branch", width: 180 },
        { field: "charge_amount", headerName: "Charge Amount", width: 140 },
        { field: "item_ref_code", headerName: "Item Ref Code", width: 160 },
        { field: "item_name", headerName: "Item Name", width: 180 },
        { field: "item_price", headerName: "Item Price", width: 130 },
        {
            field: "status",
            headerName: "Status",
            width: 120,
            renderCell: (params) => (
                <span style={{ color: Number(params.value) === 1 ? "green" : "red", fontWeight: 600 }}>
                    {Number(params.value) === 1 ? "Active" : "Inactive"}
                </span>
            ),
        },
        { field: "created_by_name", headerName: "Created By", width: 160 },
        { field: "updated_by_name", headerName: "Updated By", width: 160 },
        { field: "updated_at", headerName: "Updated At", width: 170 },
        {
            field: "actions",
            headerName: "Actions",
            width: 220,
            sortable: false,
            renderCell: (params) => (
                <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                    <Button size="sm" variant="primary" onClick={() => openEditModal(params.row)}>
                        Edit
                    </Button>
                    <Button size="sm" variant="info" onClick={() => openHistoryModal(params.row)}>
                        History
                    </Button>
                    <Button
                        size="sm"
                        variant={Number(params.row.status) === 1 ? "outline-danger" : "outline-success"}
                        onClick={() => toggleStatus(params.row)}
                    >
                        {Number(params.row.status) === 1 ? "Disable" : "Enable"}
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <Container fluid className="mt-3">
            <DynamicBreadcrumb pathArray={pathArray} />

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 style={{ margin: 0 }}>Shipping Charge Item Mapping</h5>
                <Button onClick={openAddModal}>Add Mapping</Button>
            </div>

            <div className="p-3 mb-3 bg-white rounded shadow-sm">
                <Row className="g-3">
                    <Col md={3}>
                        <Form.Label className="mb-1">Scope</Form.Label>
                        <Form.Select value={scope} onChange={(e) => setScope(e.target.value)}>
                            <option value="ALL">All</option>
                            <option value="GLOBAL">GLOBAL</option>
                            <option value="BRANCH">BRANCH</option>
                        </Form.Select>
                    </Col>

                    <Col md={3}>
                        <Form.Label className="mb-1">Status</Form.Label>
                        <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="ALL">All</option>
                            <option value="1">Active</option>
                            <option value="0">Inactive</option>
                        </Form.Select>
                    </Col>

                    <Col md={3}>
                        <Form.Label className="mb-1">Charge Type</Form.Label>
                        <Form.Select value={chargeType} onChange={(e) => setChargeType(e.target.value)}>
                            <option value="ALL">All</option>
                            <option value="DELIVERY_CHARGE">Delivery Charge</option>
                            <option value="SMART_CART_FEE">Smart Cart Fee</option>
                        </Form.Select>
                    </Col>

                    <Col md={3}>
                        <Form.Label className="mb-1">Branch (optional)</Form.Label>
                        <SearchableDropdown
                            options={allBranches}
                            extraoption={{ branchId: 0, branchName: "All" }}
                            label="branchName"
                            id="branchId"
                            selectedVal={branchName}
                            handleChange={(v) => {
                                setBranchId(v?.branchId ?? 0);
                                setBranchName(v?.branchName ?? "All");
                            }}
                        />
                    </Col>
                </Row>
            </div>

            <Box
                sx={{
                    height: 650,
                    width: "100%",
                    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
                    [`& .${gridClasses.row}`]: { borderBottom: "1px solid #f0f0f0" },
                }}
            >
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    disableRowSelectionOnClick
                    sortingMode="server"
                    filterMode="server"
                    paginationMode="server"
                    rowCount={totalRecords}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    pageSizeOptions={[10, 15, 25, 50]}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } },
                    }}
                />
            </Box>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingRow ? "Edit Mapping" : "Add Mapping"}</Modal.Title>
                </Modal.Header>

                <Form onSubmit={handleSave}>
                    <Modal.Body>
                        <Row className="g-3">
                            <Col md={4}>
                                <Form.Label>Scope</Form.Label>
                                <Form.Select
                                    value={form.scope}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setForm((p) => ({
                                            ...p,
                                            scope: v,
                                            branch_id: v === "BRANCH" ? p.branch_id : null,
                                        }));
                                    }}
                                    isInvalid={!!errors.scope}
                                >
                                    <option value="GLOBAL">GLOBAL</option>
                                    <option value="BRANCH">BRANCH</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{errors.scope}</Form.Control.Feedback>
                            </Col>

                            <Col md={8}>
                                <Form.Label>Branch (only for BRANCH scope)</Form.Label>
                                {form.scope === "BRANCH" ? (
                                    <SearchableDropdown
                                        options={allBranches}
                                        label="branchName"
                                        id="branchId"
                                        selectedVal={
                                            form.branch_id
                                                ? allBranches.find((b) => Number(b.branchId) === Number(form.branch_id))?.branchName || ""
                                                : ""
                                        }
                                        handleChange={(v) => {
                                            setForm((p) => ({ ...p, branch_id: v?.branchId || null }));
                                        }}
                                    />
                                ) : (
                                    <Form.Control value="(Not required for GLOBAL)" disabled />
                                )}
                                {errors.branch_id && (
                                    <div style={{ color: "#dc3545", fontSize: 12, marginTop: 4 }}>
                                        {errors.branch_id}
                                    </div>
                                )}
                            </Col>

                            <Col md={3}>
                                <Form.Label>Charge Type</Form.Label>
                                <Form.Select
                                    value={form.charge_type}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            charge_type: e.target.value,
                                            item_name:
                                                e.target.value === "SMART_CART_FEE"
                                                    ? "Smart Cart Fee"
                                                    : "Shipping Charge",
                                        }))
                                    }
                                    isInvalid={!!errors.charge_type}
                                >
                                    <option value="DELIVERY_CHARGE">Delivery Charge</option>
                                    <option value="SMART_CART_FEE">Smart Cart Fee</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{errors.charge_type}</Form.Control.Feedback>
                            </Col>

                            <Col md={3}>
                                <Form.Label>Charge Amount</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={form.charge_amount}
                                    onChange={(e) => setForm((p) => ({ ...p, charge_amount: e.target.value }))}
                                    isInvalid={!!errors.charge_amount}
                                    placeholder="30.00"
                                />
                                <Form.Control.Feedback type="invalid">{errors.charge_amount}</Form.Control.Feedback>
                            </Col>

                            <Col md={3}>
                                <Form.Label>Item Price</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={form.item_price}
                                    onChange={(e) => setForm((p) => ({ ...p, item_price: e.target.value }))}
                                    isInvalid={!!errors.item_price}
                                    placeholder="30.00"
                                />
                                <Form.Control.Feedback type="invalid">{errors.item_price}</Form.Control.Feedback>
                            </Col>

                            <Col md={3}>
                                <Form.Label>Item Ref Code</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={form.item_ref_code}
                                    onChange={(e) => setForm((p) => ({ ...p, item_ref_code: e.target.value }))}
                                    isInvalid={!!errors.item_ref_code}
                                    placeholder="10005365"
                                />
                                <Form.Control.Feedback type="invalid">{errors.item_ref_code}</Form.Control.Feedback>
                            </Col>

                            <Col md={3}>
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                    value={form.status}
                                    onChange={(e) => setForm((p) => ({ ...p, status: Number(e.target.value) }))}
                                >
                                    <option value={1}>Active</option>
                                    <option value={0}>Inactive</option>
                                </Form.Select>
                            </Col>

                            <Col md={9}>
                                <Form.Label>Item Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={form.item_name}
                                    onChange={(e) => setForm((p) => ({ ...p, item_name: e.target.value }))}
                                    isInvalid={!!errors.item_name}
                                    placeholder="Shipping Charge"
                                />
                                <Form.Control.Feedback type="invalid">{errors.item_name}</Form.Control.Feedback>
                            </Col>
                        </Row>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            {editingRow ? "Update" : "Create"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal
                show={showHistoryModal}
                onHide={() => setShowHistoryModal(false)}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Mapping History</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {historyLoading ? (
                        <div className="text-center py-4">Loading...</div>
                    ) : historyRows.length === 0 ? (
                        <div className="text-center py-4">No history found</div>
                    ) : (
                        <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
                            <table className="table table-bordered table-sm">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Action</th>
                                        <th>Changed By</th>
                                        <th>Changed IP</th>
                                        <th>Changed At</th>
                                        <th>Old Data</th>
                                        <th>New Data</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyRows.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.id}</td>
                                            <td>{row.action}</td>
                                            <td>{row.changed_by_name}</td>
                                            <td>{row.changed_ip}</td>
                                            <td>{row.created_at}</td>
                                            <td>
                                                <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, margin: 0 }}>
                                                    {row.old_data}
                                                </pre>
                                            </td>
                                            <td>
                                                <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, margin: 0 }}>
                                                    {row.new_data}
                                                </pre>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {loaderVisible ? <Loader /> : null}
        </Container>
    );
};

export default ShippingChargeItemMappingsList;